import React, { useState, useCallback, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Plus, Loader2, AlertTriangle } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useQueryState, useQueryStates, parseAsInteger, parseAsString, parseAsArrayOf, createParser } from 'nuqs'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { SalesInvoiceDataTable } from './SalesInvoiceDataTable'
import {
  columns,
  editSalesInvoiceEvent,
  deleteSalesInvoiceEvent,
  markAsPaidSalesInvoiceEvent,
  duplicateSalesInvoiceEvent,
  manageDeliveryChallanEvent
} from './columns'
import { listVouchers, deleteVoucher, getVoucher } from '@/app/services/voucherService'
import { VoucherResponse } from '@/app/types/voucher'
import { useOrganization } from '@/app/hooks/useOrganization'
import { PaginatedResponse, ListQueryParams } from '@/app/types/api'
import { SortingState } from '@tanstack/react-table'
import { getBackendStatusFilters, SimplifiedSalesInvoiceStatus } from '@/app/lib/statusUtils'
import { QuickPaymentModal } from '../components/QuickPaymentModal'
// HIDDEN FOR AUTOMOTIVE: Delivery modals
// import { DeliveryChallanManagementModal } from '../components/DeliveryChallanManagementModal'
// import { BulkDeliveryUpdateModal } from '../components/BulkDeliveryUpdateModal'
// import { BulkAgentAssignmentModal } from '../components/BulkAgentAssignmentModal'
import { usePreferenceStore } from '@/app/store/preferenceStore'
import { useDeliveryFilterStore } from '@/app/store/deliveryFilterStore'
import { DocumentService } from '@/app/services/documentService'
import { DocumentStatusEnum } from '@/app/types/document'

// Extended PaginatedResponse with page count (backend might return different field names)
interface ExtendedPaginatedResponse<T> extends PaginatedResponse<T> {
  page_count?: number
  pageCount?: number
  total_pages?: number
  totalPages?: number
}

// Define the sort direction type
type SortDirection = 'asc' | 'desc'

// Custom parser for sorting state
const sortingParser = createParser({
  parse: (value: string) => {
    if (!value) return [{ id: 'created_at', desc: true }]
    const [field, direction] = value.split('-')
    return [{
      id: field || 'created_at',
      desc: direction === 'desc'
    }]
  },
  serialize: (value: SortingState) => {
    if (!value.length) return 'created_at-desc'
    const sort = value[0]
    return `${sort.id}-${sort.desc ? 'desc' : 'asc'}`
  }
})

// Custom parser for page (0-based internal, 1-based in URL)
const pageParser = createParser({
  parse: (value: string) => {
    const page = parseInt(value, 10)
    return isNaN(page) || page < 1 ? 0 : page - 1 // Convert 1-based to 0-based
  },
  serialize: (value: number) => {
    return String(value + 1) // Convert 0-based to 1-based
  }
})

/**
 * Filter Configuration
 * -------------------
 * This configuration controls how filters are built for the API.
 *
 * To change the filter format globally, update FILTER_CONFIG.format:
 * - 'array': Uses array of objects with field, value, op (best for advanced filtering)
 *   Example: [{"field":"party.name","value":"%han%","op":"ilike"}]
 *
 * - 'object': Uses simple key-value pairs (best for exact matches)
 *   Example: {"party.name":"haniffa","status":"Paid"}
 *
 * To add special handling for a field:
 * 1. Add an entry to FILTER_CONFIG.fields with the field name
 * 2. Configure options like:
 *    - op: The operator to use (eq, like, ilike, gt, lt, etc.)
 *    - field: Map to a different backend field name
 *    - transform: Function to transform the value (e.g., add wildcards)
 *    - specialCase: Function for custom handling based on value
 */
// Filter configuration object for consistent filtering approach
const FILTER_CONFIG = {
  // Filter format to use: 'array' (with field/value/op objects) or 'object' (simple key-value pairs)
  format: 'array' as const,

  // Special field configurations
  fields: {
    'voucher_number': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for case-insensitive contains search
    },
    'party.name': {
      op: 'ilike',
      transform: (value: string): string => `%${value}%` // Wrap with wildcards for contains search
    },
    status: {
      // Special mapping for status filters
      specialCase: (
        value: string
      ):
        | { field: string; value: string | number | boolean | string[]; op: string }
        | { field: string; value: string | number | boolean | string[]; op: string }[]
        | null => {
        // Use the reverse mapping function to get backend filters
        const backendFilters = getBackendStatusFilters(value as SimplifiedSalesInvoiceStatus)
        return backendFilters.length > 0 ? backendFilters : null
      }
    },
    delivery_progress: {
      // Map to actual backend field
      field: 'delivery_status',
      op: 'in' // Use 'in' operator for multi-select
    },
    'site_id': {
      op: 'eq'
    },
    'total_amount': {
      op: 'eq',
      transform: (value: string | number): number => {
        const numValue = Number(value);
        return isNaN(numValue) ? 0 : numValue;
      }
    }
  }
}

// Define filter object types
interface FilterObject {
  field: string
  value: string | number | boolean | string[]
  op: string
}

// Helper function to build filter parameters based on config
const buildFilterParams = (
  filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }>,
  config = FILTER_CONFIG
): string => {
  console.log('buildFilterParams called with filters:', filters)

  if (config.format === 'array') {
    // Build array of filter objects with field, value, op
    const filterArray: (FilterObject | FilterObject[] | null)[] = Object.entries(filters).map(
      ([field, value]) => {
        // Skip empty values
        if (value === undefined || value === null || value === '') {
          return null
        }

        // Handle date range filters
        if ((field === 'transaction_date' || field === 'created_at') && typeof value === 'object' && 'from' in value) {
          const dateFilter = value as { from?: string; to?: string }
          const dateFilters: FilterObject[] = []

          console.log('Processing date filter:', dateFilter)

          if (dateFilter.from) {
            // Ensure we add time component for start of day
            const fromDate = `${dateFilter.from} 00:00:00`
            dateFilters.push({
              field: field, // Use the actual field (created_at or transaction_date)
              value: fromDate,
              op: 'gte' // Greater than or equal
            })
          }

          if (dateFilter.to) {
            // Ensure we add time component for end of day
            const toDate = `${dateFilter.to} 23:59:59`
            dateFilters.push({
              field: field, // Use the actual field (created_at or transaction_date)
              value: toDate,
              op: 'lte' // Less than or equal
            })
          }

          console.log('Generated date filters:', dateFilters)
          return dateFilters.length > 0 ? dateFilters : null
        }

        // Handle array values (for multi-select filters like delivery_progress)
        if (Array.isArray(value)) {
          // Skip empty arrays
          if (value.length === 0) {
            return null
          }
          
          const fieldConfig = config.fields[field as keyof typeof config.fields]
          const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field
          const op = (fieldConfig && 'op' in fieldConfig) ? fieldConfig.op : 'in'
          
          return {
            field: actualField,
            value: value, // Send array as-is
            op: op
          } as FilterObject
        }

        // Check for special case handling
        const fieldConfig = config.fields[field as keyof typeof config.fields]
        if (fieldConfig && 'specialCase' in fieldConfig && fieldConfig.specialCase) {
          const specialCase = fieldConfig.specialCase(String(value))
          if (specialCase) {
            // Special case can return either a single filter or an array of filters
            return specialCase as FilterObject | FilterObject[]
          }
        }

        // Get actual field name from config or use the original
        const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field

        // Transform value if needed
        const transformedValue = (fieldConfig && 'transform' in fieldConfig && fieldConfig.transform)
          ? fieldConfig.transform(String(value))
          : String(value)

        // Use configured operator or default to 'eq'
        const op = (fieldConfig && 'op' in fieldConfig) ? fieldConfig.op : 'eq'

        return {
          field: actualField,
          value: transformedValue,
          op // Use 'op' instead of 'operator'
        } as FilterObject
      }
    )

    // Flatten the array (to handle date range filters that return arrays)
    const flattenedArray = filterArray.flatMap((f) => f || []).filter(Boolean)

    const result = JSON.stringify(flattenedArray)
    console.log('Final filter JSON:', result)
    return result
  } else {
    // Simple object format for exact matches
    const filterObj: Record<string, string | number | boolean> = {}

    Object.entries(filters).forEach(([field, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Apply field mapping if configured
        const fieldConfig = config.fields[field as keyof typeof config.fields]
        const actualField = (fieldConfig && 'field' in fieldConfig) ? fieldConfig.field : field
        // For object format, only accept simple types
        if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
          filterObj[actualField] = value
        }
      }
    })

    return JSON.stringify(filterObj)
  }
}

const SalesInvoicesPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { currentOrganizationId } = useOrganization()
  const { defaultSiteId } = usePreferenceStore()
  const navigate = useNavigate()

  // Use nuqs for URL state management
  const [page, setPage] = useQueryState('page', pageParser.withDefault(0))
  const [search, setSearch] = useQueryState('search', parseAsString.withDefault(''))
  const [sorting, setSorting] = useQueryState('sortBy', sortingParser.withDefault([{ id: 'created_at', desc: true }]))
  
  // Use nuqs for filter state management
  const [filterStates, setFilterStates] = useQueryStates({
    voucher_number: parseAsString,
    party_name: parseAsString, // Changed from 'party.name' to avoid URL encoding issues
    status: parseAsString,
    delivery_progress: parseAsArrayOf(parseAsString),
    transaction_date_from: parseAsString,
    transaction_date_to: parseAsString,
    created_at_from: parseAsString,
    created_at_to: parseAsString,
    total_amount: parseAsString,
  }, {
    // Use shallow routing to prevent full page reloads
    shallow: true,
  })

  // Listen to Zustand store for delivery filter changes from Header badges
  const { pendingFilter, clearPendingFilter } = useDeliveryFilterStore()

  useEffect(() => {
    if (pendingFilter) {
      // Update ONLY the delivery_progress filter without spreading old state
      // This prevents nuqs from fighting with stale state values
      setFilterStates({ delivery_progress: [pendingFilter] })
      setPage(0)
      // Clear the pending filter after applying it
      clearPendingFilter()
    }
  }, [pendingFilter, setFilterStates, setPage, clearPendingFilter])

  // Build filters object from nuqs state
  const filters: Record<string, string | number | boolean | string[] | { from?: string; to?: string }> = {}
  
  // Add simple filters
  if (filterStates.voucher_number) filters.voucher_number = filterStates.voucher_number
  if (filterStates.party_name) filters['party.name'] = filterStates.party_name // Map party_name to party.name
  if (filterStates.status) filters.status = filterStates.status
  if (filterStates.delivery_progress) filters.delivery_progress = filterStates.delivery_progress
  if (filterStates.total_amount) filters.total_amount = filterStates.total_amount
  
  // Add date range filters
  if (filterStates.transaction_date_from || filterStates.transaction_date_to) {
    filters.transaction_date = {
      from: filterStates.transaction_date_from || undefined,
      to: filterStates.transaction_date_to || undefined
    }
  }
  if (filterStates.created_at_from || filterStates.created_at_to) {
    filters.created_at = {
      from: filterStates.created_at_from || undefined,
      to: filterStates.created_at_to || undefined
    }
  }

  // State for dialogs only
  const [limit] = useState<number>(10)

  // Update document title
  useEffect(() => {
    document.title = 'Sales Invoices'
    return () => {
      document.title = 'Billing App'
    }
  }, [])

  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState<boolean>(false)
  const [selectedSalesInvoice, setSelectedSalesInvoice] = useState<VoucherResponse | null>(null)
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false)
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] =
    useState<VoucherResponse | null>(null)
  const [isDeliveryChallanModalOpen, setIsDeliveryChallanModalOpen] = useState<boolean>(false)
  const [selectedInvoiceForDC, setSelectedInvoiceForDC] = useState<VoucherResponse | null>(null)
  
  // Bulk delivery update state
  const [isBulkDeliveryModalOpen, setIsBulkDeliveryModalOpen] = useState<boolean>(false)
  const [selectedInvoicesForBulk, setSelectedInvoicesForBulk] = useState<VoucherResponse[]>([])
  const [bulkTargetStatus, setBulkTargetStatus] = useState<'packed' | 'shipped' | 'delivered'>('packed')
  const [isBulkProcessing, setIsBulkProcessing] = useState<boolean>(false)
  const [bulkProgress, setBulkProgress] = useState<{
    current: number
    total: number
    currentInvoice?: string
    errors?: string[]
  } | undefined>()

  // Bulk agent assignment state
  const [isBulkAgentModalOpen, setIsBulkAgentModalOpen] = useState<boolean>(false)
  const [selectedInvoicesForAgent, setSelectedInvoicesForAgent] = useState<VoucherResponse[]>([])
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null)
  const [isBulkAgentProcessing, setIsBulkAgentProcessing] = useState<boolean>(false)
  const [bulkAgentProgress, setBulkAgentProgress] = useState<{
    current: number
    total: number
    currentInvoice?: string
    skipped?: number
    errors?: string[]
  } | undefined>()

  // Determine sort parameters for API call
  const getSortParams = useCallback(() => {
    if (sorting.length === 0)
      return {
        sort_by: 'created_at',
        sort_dir: 'desc' as SortDirection
      }

    const sort = sorting[0]
    const direction: SortDirection = sort.desc ? 'desc' : 'asc'

    return {
      sort_by: sort.id,
      sort_dir: direction
    }
  }, [sorting])

  // Handle filter changes
  const handleFilterChange = useCallback((newFilters: Record<string, unknown>): void => {
    console.log('handleFilterChange called with:', newFilters)
    
    // Build the new filter states for nuqs
    const updates: Record<string, string | string[] | null> = {}
    let hasActualChanges = false

    // List of all possible filter keys that could come from the DataTable
    const knownFilterKeys = ['voucher_number', 'party.name', 'status', 'delivery_progress', 'transaction_date', 'created_at', 'total_amount']
    
    // First, check all known keys and set to null if not present in newFilters
    knownFilterKeys.forEach(key => {
      if (!(key in newFilters)) {
        const mappedKey = key === 'party.name' ? 'party_name' : key
        
        // For date range filters, clear both from and to
        if (key === 'transaction_date' || key === 'created_at') {
          if (filterStates[`${key}_from`] !== null || filterStates[`${key}_to`] !== null) {
            hasActualChanges = true
            updates[`${key}_from`] = null
            updates[`${key}_to`] = null
          }
        } else if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
          updates[mappedKey] = null
        }
      }
    })

    Object.entries(newFilters).forEach(([key, value]) => {
      // Map 'party.name' to 'party_name' for URL compatibility
      const mappedKey = key === 'party.name' ? 'party_name' : key
      
      // Handle date range object
      if (typeof value === 'object' && value !== null && 'from' in value) {
        const dateRange = value as { from?: string; to?: string }
        const fromKey = `${key}_from`
        const toKey = `${key}_to`
        const newFrom = dateRange.from || null
        const newTo = dateRange.to || null
        
        // Check if values actually changed
        if (filterStates[fromKey] !== newFrom || filterStates[toKey] !== newTo) {
          hasActualChanges = true
        }
        
        updates[fromKey] = newFrom
        updates[toKey] = newTo
      } else if (Array.isArray(value)) {
        // Handle arrays for multi-select filters
        const newValue = value.length > 0 ? value : null
        const currentValue = filterStates[mappedKey]
        
        // Check if array values changed
        if (JSON.stringify(currentValue) !== JSON.stringify(newValue)) {
          hasActualChanges = true
        }
        
        updates[mappedKey] = newValue
      } else if (value === undefined || value === null || value === '') {
        // Clear the filter
        if (filterStates[mappedKey] !== null) {
          hasActualChanges = true
        }
        updates[mappedKey] = null
      } else {
        // Handle simple string/number values
        const newValue = String(value)
        if (filterStates[mappedKey] !== newValue) {
          hasActualChanges = true
        }
        updates[mappedKey] = newValue
      }
    })

    console.log('Updating filters with:', updates, 'hasActualChanges:', hasActualChanges)
    console.log('Current filterStates:', filterStates)
    
    // Update filters using nuqs
    setFilterStates(updates)
    
    // Only reset to first page when filters actually change
    if (hasActualChanges) {
      setPage(0)
    }
  }, [setFilterStates, setPage])

  // Fetch sales invoices with search, pagination, sorting and filters
  const {
    data: salesInvoicesData,
    isLoading,
    isError,
    error
  } = useQuery<ExtendedPaginatedResponse<VoucherResponse>>({
    queryKey: ['sales-invoices', page, limit, search, sorting, filters, currentOrganizationId, defaultSiteId],
    queryFn: () => {
      const { sort_by, sort_dir } = getSortParams()
      const skip = page * limit // Calculate skip based on page and limit

      // Create params object with basic query parameters
      const params: ListQueryParams = {
        skip,
        limit,
        sort_by,
        sort_dir,
        voucher_type_id: 1
      }

      // Add search if provided
      if (search) {
        params.search_query = search
        // Also add search_fields to search across multiple fields
        params.search_fields = JSON.stringify(['voucher_number', 'party.name', 'reference_number'])
      }

      // Handle filters
      const activeFilters = { ...filters }
      
      // Add default site filter if user has one set (not visible in UI)
      if (defaultSiteId) {
        activeFilters.site_id = defaultSiteId
      }
      
      if (Object.keys(activeFilters).length > 0) {
        console.log('Current filters state:', activeFilters)
        // Build filter parameters based on configuration
        params.filters = buildFilterParams(activeFilters)
        console.log('Filter JSON:', params.filters)
      }

      console.log('API Query Params:', params)
      return listVouchers(params)
    },
    enabled: !!currentOrganizationId
  })

  // Calculate page count from total items and limit if not directly provided
  const pageCount =
    salesInvoicesData?.page_count ??
    salesInvoicesData?.pageCount ??
    salesInvoicesData?.total_pages ??
    salesInvoicesData?.totalPages ??
    (salesInvoicesData ? Math.ceil(salesInvoicesData.total / limit) : 0)

  // Delete sales invoice mutation
  const deleteSalesInvoiceMutation = useMutation({
    mutationFn: (id: number) => deleteVoucher(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })
      setIsDeleteAlertOpen(false)
      setSelectedSalesInvoice(null)
      toast.success('Sales invoice deleted successfully')
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete sales invoice: ${error.message}`)
    }
  })


  // Event handlers for table actions
  useEffect(() => {
    const handleEditSalesInvoice = (
      event: CustomEvent<{ salesInvoice: VoucherResponse }>
    ): void => {
      // Use navigate instead of window.location.href
      navigate(`/sales-invoices/detail/${event.detail.salesInvoice.id}`)
    }

    const handleDeleteSalesInvoice = (
      event: CustomEvent<{ salesInvoice: VoucherResponse }>
    ): void => {
      setSelectedSalesInvoice(event.detail.salesInvoice)
      setIsDeleteAlertOpen(true)
    }

    const handleMarkAsPaidSalesInvoice = (
      event: CustomEvent<{ salesInvoice: VoucherResponse }>
    ): void => {
      setSelectedInvoiceForPayment(event.detail.salesInvoice)
      setIsPaymentModalOpen(true)
    }

    const handleDuplicateSalesInvoice = (
      event: CustomEvent<{ salesInvoice: VoucherResponse }>
    ): void => {
      const salesInvoice = event.detail.salesInvoice
      
      // Prepare invoice data for duplication
      const duplicateData = {
        party_id: salesInvoice.party_id,
        site_id: salesInvoice.site_id,
        reference_number: '', // Clear reference number for new invoice
        narration: `Duplicated from Invoice #${salesInvoice.voucher_number}`,
        items: salesInvoice.items || [],
        // Reset dates to current
        transaction_date: new Date(),
        due_date: null,
        voucher_type_id: salesInvoice.voucher_type_id,
        total_amount: salesInvoice.total_amount
      }
      
      // Navigate to create page with the duplicate data
      navigate('/sales-invoices/new', { 
        state: { 
          duplicateInvoice: duplicateData,
          fromDuplicate: true 
        } 
      })
      toast.info('Invoice duplicated. Review and save the new invoice.')
    }

    const handleManageDeliveryChallan = (
      event: CustomEvent<{ salesInvoice: VoucherResponse }>
    ): void => {
      setSelectedInvoiceForDC(event.detail.salesInvoice)
      setIsDeliveryChallanModalOpen(true)
    }


    document.addEventListener(editSalesInvoiceEvent, handleEditSalesInvoice as EventListener)
    document.addEventListener(deleteSalesInvoiceEvent, handleDeleteSalesInvoice as EventListener)
    document.addEventListener(
      markAsPaidSalesInvoiceEvent,
      handleMarkAsPaidSalesInvoice as EventListener
    )
    document.addEventListener(
      duplicateSalesInvoiceEvent,
      handleDuplicateSalesInvoice as EventListener
    )
    document.addEventListener(
      manageDeliveryChallanEvent,
      handleManageDeliveryChallan as EventListener
    )

    return () => {
      document.removeEventListener(editSalesInvoiceEvent, handleEditSalesInvoice as EventListener)
      document.removeEventListener(
        deleteSalesInvoiceEvent,
        handleDeleteSalesInvoice as EventListener
      )
      document.removeEventListener(
        markAsPaidSalesInvoiceEvent,
        handleMarkAsPaidSalesInvoice as EventListener
      )
      document.removeEventListener(
        duplicateSalesInvoiceEvent,
        handleDuplicateSalesInvoice as EventListener
      )
      document.removeEventListener(
        manageDeliveryChallanEvent,
        handleManageDeliveryChallan as EventListener
      )
    }
  }, [navigate])

  // Handle search input change
  const handleSearchChange = useCallback((value: string): void => {
    setSearch(value)
    setPage(0) // Reset to first page on search
  }, [setSearch, setPage])

  // Handle sorting change
  const handleSortingChange = useCallback((newSorting: SortingState): void => {
    setSorting(newSorting)
    setPage(0) // Reset to first page on sort change
  }, [setSorting, setPage])

  // Handle page change
  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage)
  }, [setPage])

  // Handle delete confirmation
  const handleDeleteSalesInvoice = (): void => {
    if (selectedSalesInvoice) {
      deleteSalesInvoiceMutation.mutate(selectedSalesInvoice.id)
    }
  }

  // Handle bulk delivery status update
  const handleBulkAction = (action: string, selectedRows: VoucherResponse[]): void => {
    if (action === 'assign-agent') {
      setSelectedInvoicesForAgent(selectedRows)
      setSelectedAgentId(null)
      setIsBulkAgentModalOpen(true)
      setBulkAgentProgress(undefined)
    } else {
      setSelectedInvoicesForBulk(selectedRows)
      setBulkTargetStatus(action as 'packed' | 'shipped' | 'delivered')
      setIsBulkDeliveryModalOpen(true)
      setBulkProgress(undefined)
    }
  }

  // Process bulk delivery status update
  const processBulkDeliveryUpdate = async (): Promise<void> => {
    setIsBulkProcessing(true)
    const documentService = new DocumentService()
    const errors: string[] = []
    
    // Filter out cancelled invoices
    const validInvoices = selectedInvoicesForBulk.filter(inv => !inv.is_cancelled)
    
    setBulkProgress({
      current: 0,
      total: validInvoices.length,
      errors: []
    })

    // Map target status to DocumentStatusEnum
    const statusMap: Record<string, DocumentStatusEnum> = {
      'packed': DocumentStatusEnum.PACKED,
      'shipped': DocumentStatusEnum.SHIPPED,
      'delivered': DocumentStatusEnum.DELIVERED
    }
    const targetStatus = statusMap[bulkTargetStatus]

    for (let i = 0; i < validInvoices.length; i++) {
      const invoice = validInvoices[i]
      
      setBulkProgress(prev => ({
        current: i + 1,
        total: validInvoices.length,
        currentInvoice: invoice.voucher_number,
        errors: prev?.errors || []
      }))

      try {
        // First, fetch the complete invoice data with delivery challans
        const fullInvoice = await getVoucher(invoice.id)
        
        // Check if invoice has delivery challans
        if (fullInvoice.related_delivery_challans && fullInvoice.related_delivery_challans.length > 0) {
          // Update each delivery challan
          for (const dc of fullInvoice.related_delivery_challans) {
            // Skip if DC is already at the target status
            if (dc.status === targetStatus || 
                (targetStatus === DocumentStatusEnum.DELIVERED && dc.status === 'delivered') ||
                (targetStatus === DocumentStatusEnum.PACKED && dc.status === 'packed') ||
                (targetStatus === DocumentStatusEnum.SHIPPED && dc.status === 'shipped')) {
              console.log(`DC ${dc.document_number} already at status ${dc.status}, skipping`)
              continue
            }
            
            console.log(`Updating DC ${dc.document_number} from ${dc.status} to ${targetStatus}`)
            await documentService.updateDeliveryChallanStatus(
              dc.id,
              targetStatus,
              `Bulk update via sales invoice list`
            )
          }
        } else {
          // Create a delivery challan first
          console.log(`Creating DC for invoice ${invoice.voucher_number}`)
          const newDC = await documentService.createDeliveryChallanFromVoucher(invoice.id)
          
          // Then update its status if needed (default created status is usually 'draft' or 'confirmed')
          if (targetStatus !== DocumentStatusEnum.CONFIRMED && targetStatus !== DocumentStatusEnum.DRAFT) {
            console.log(`Updating newly created DC ${newDC.document_number} to ${targetStatus}`)
            await documentService.updateDeliveryChallanStatus(
              newDC.id,
              targetStatus,
              `Bulk update via sales invoice list`
            )
          }
        }
      } catch (error) {
        console.error(`Error processing invoice ${invoice.voucher_number}:`, error)
        const errorMessage = `Failed to update ${invoice.voucher_number}: ${error instanceof Error ? error.message : 'Unknown error'}`
        errors.push(errorMessage)
        setBulkProgress(prev => ({
          current: prev?.current || i + 1,
          total: validInvoices.length,
          currentInvoice: invoice.voucher_number,
          errors: [...(prev?.errors || []), errorMessage]
        }))
      }
    }

    // Final progress update
    setBulkProgress({
      current: validInvoices.length,
      total: validInvoices.length,
      errors
    })

    // Refresh the invoice list
    await queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })
    
    // Show success/error toast
    if (errors.length === 0) {
      toast.success(`Successfully updated ${validInvoices.length} invoice(s) to ${bulkTargetStatus}`)
    } else if (errors.length === validInvoices.length) {
      toast.error('Failed to update all invoices. Check console for details.')
    } else {
      toast.warning(`Updated ${validInvoices.length - errors.length} of ${validInvoices.length} invoices`)
    }

    setIsBulkProcessing(false)

    // Close modal after a delay if successful
    if (errors.length === 0) {
      setTimeout(() => {
        setIsBulkDeliveryModalOpen(false)
        setBulkProgress(undefined)
      }, 1500)
    }
  }

  // Process bulk agent assignment
  const processBulkAgentAssignment = async (): Promise<void> => {
    setIsBulkAgentProcessing(true)
    const documentService = new DocumentService()
    const errors: string[] = []
    let skippedCount = 0

    // Filter out cancelled invoices
    const validInvoices = selectedInvoicesForAgent.filter(inv => !inv.is_cancelled)

    setBulkAgentProgress({
      current: 0,
      total: validInvoices.length,
      skipped: 0,
      errors: []
    })

    for (let i = 0; i < validInvoices.length; i++) {
      const invoice = validInvoices[i]

      setBulkAgentProgress(prev => ({
        current: i + 1,
        total: validInvoices.length,
        currentInvoice: invoice.voucher_number,
        skipped: prev?.skipped || 0,
        errors: prev?.errors || []
      }))

      try {
        // First, fetch the complete invoice data with delivery challans
        const fullInvoice = await getVoucher(invoice.id)

        // Check if invoice has delivery challans
        if (fullInvoice.related_delivery_challans && fullInvoice.related_delivery_challans.length > 0) {
          // Update each delivery challan's agent
          for (const dc of fullInvoice.related_delivery_challans) {
            console.log(`Updating DC ${dc.document_number} agent to ${selectedAgentId || 'unassigned'}`)
            await documentService.updateDeliveryChallanAgent(dc.id, selectedAgentId)
          }
        } else {
          // Skip invoices without delivery challans
          console.log(`Skipping invoice ${invoice.voucher_number} - no delivery challans`)
          skippedCount++
          setBulkAgentProgress(prev => ({
            current: prev?.current || i + 1,
            total: validInvoices.length,
            currentInvoice: invoice.voucher_number,
            skipped: (prev?.skipped || 0) + 1,
            errors: prev?.errors || []
          }))
        }
      } catch (error) {
        console.error(`Error processing invoice ${invoice.voucher_number}:`, error)
        const errorMessage = `Failed to update ${invoice.voucher_number}: ${error instanceof Error ? error.message : 'Unknown error'}`
        errors.push(errorMessage)
        setBulkAgentProgress(prev => ({
          current: prev?.current || i + 1,
          total: validInvoices.length,
          currentInvoice: invoice.voucher_number,
          skipped: prev?.skipped || 0,
          errors: [...(prev?.errors || []), errorMessage]
        }))
      }
    }

    // Final progress update
    setBulkAgentProgress({
      current: validInvoices.length,
      total: validInvoices.length,
      skipped: skippedCount,
      errors
    })

    // Refresh the invoice list
    await queryClient.invalidateQueries({ queryKey: ['sales-invoices'] })

    // Show success/error toast
    const successCount = validInvoices.length - errors.length - skippedCount
    if (errors.length === 0) {
      if (skippedCount > 0) {
        toast.success(`Successfully updated ${successCount} invoice(s). Skipped ${skippedCount} without delivery challans.`)
      } else {
        toast.success(`Successfully assigned agent to ${successCount} invoice(s)`)
      }
    } else if (errors.length === validInvoices.length) {
      toast.error('Failed to update all invoices. Check console for details.')
    } else {
      toast.warning(`Updated ${successCount} of ${validInvoices.length} invoices. Skipped ${skippedCount}, ${errors.length} errors.`)
    }

    setIsBulkAgentProcessing(false)

    // Close modal after a delay if successful
    if (errors.length === 0) {
      setTimeout(() => {
        setIsBulkAgentModalOpen(false)
        setBulkAgentProgress(undefined)
      }, 1500)
    }
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Sales Invoices</h1>
        <Button asChild>
          <Link to="/sales-invoices/detail">
            <Plus className="h-4 w-4 mr-2" />
            New Sales Invoice
          </Link>
        </Button>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading sales invoices: {(error as Error).message}
          </AlertDescription>
        </Alert>
      )}

      {/* Sales Invoices Data Table */}
      <SalesInvoiceDataTable
        data={salesInvoicesData?.items || []}
        columns={columns}
        isLoading={isLoading}
        pageCount={pageCount}
        page={page}
        setPage={handlePageChange}
        onSortingChange={handleSortingChange}
        sortingState={sorting}
        onFilterChange={handleFilterChange}
        initialFilters={filters}
        onBulkAction={handleBulkAction}
      />

      {/* Delete Confirmation Alert */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete sales invoice &quot;
              {selectedSalesInvoice?.voucher_number}&quot;. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteSalesInvoice}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteSalesInvoiceMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Payment Modal */}
      {selectedInvoiceForPayment && (
        <QuickPaymentModal
          isOpen={isPaymentModalOpen}
          onClose={() => {
            setIsPaymentModalOpen(false)
            setSelectedInvoiceForPayment(null)
          }}
          invoice={selectedInvoiceForPayment}
        />
      )}

      {/* HIDDEN FOR AUTOMOTIVE: Delivery Challan Management Modal */}
      {/* {selectedInvoiceForDC && (
        <DeliveryChallanManagementModal
          isOpen={isDeliveryChallanModalOpen}
          onClose={() => {
            setIsDeliveryChallanModalOpen(false)
            setSelectedInvoiceForDC(null)
          }}
          invoiceId={selectedInvoiceForDC.id}
          invoiceNumber={selectedInvoiceForDC.voucher_number}
        />
      )} */}

      {/* HIDDEN FOR AUTOMOTIVE: Bulk Delivery Update Modal */}
      {/* <BulkDeliveryUpdateModal
        isOpen={isBulkDeliveryModalOpen}
        onClose={() => {
          setIsBulkDeliveryModalOpen(false)
          setBulkProgress(undefined)
        }}
        invoices={selectedInvoicesForBulk}
        targetStatus={bulkTargetStatus}
        onConfirm={processBulkDeliveryUpdate}
        isProcessing={isBulkProcessing}
        progress={bulkProgress}
      /> */}

      {/* HIDDEN FOR AUTOMOTIVE: Bulk Agent Assignment Modal */}
      {/* <BulkAgentAssignmentModal
        isOpen={isBulkAgentModalOpen}
        onClose={() => {
          setIsBulkAgentModalOpen(false)
          setBulkAgentProgress(undefined)
          setSelectedAgentId(null)
        }}
        invoices={selectedInvoicesForAgent}
        selectedAgentId={selectedAgentId}
        onAgentChange={setSelectedAgentId}
        onConfirm={processBulkAgentAssignment}
        isProcessing={isBulkAgentProcessing}
        progress={bulkAgentProgress}
      /> */}
    </div>
  )
}

export default SalesInvoicesPage
